import { browser } from '$app/environment';
import { derived, get } from 'svelte/store';
import {
  defaultModulesAccess,
  getModulesAccess,
  type ModulesAccessResponse
} from '$lib/api/modules-access';
import { currentUser, isAuthenticated, isInitialized } from '$lib/stores/auth';
import {
  createBrowserCachedResource,
  type BrowserCachedResourceState
} from '$lib/utils/browser-cached-resource';

export type ModulesAccessState = Omit<BrowserCachedResourceState<ModulesAccessResponse>, 'value'>;

const resource = createBrowserCachedResource<ModulesAccessResponse>({
  resourceKey: 'modules-access-v2',
  defaultValue: defaultModulesAccess,
  ttlMs: 5 * 60 * 1000,
  isBrowser: browser,
  keepStaleUntilRefreshCompletes: true,
  loadFresh: async () => getModulesAccess()
});

export const modulesAccess = derived(resource, ($resource) => $resource.value);
export const modulesAccessState = derived(resource, ($resource): ModulesAccessState => ({
  status: $resource.status,
  error: $resource.error,
  hasResolved: $resource.hasResolved,
  updatedAt: $resource.updatedAt
}));

let authSignature: string | null = null;

export async function loadModulesAccess(force = false): Promise<void> {
  await resource.load(buildAuthSignature(), force);
}

export function resetModulesAccess(): void {
  resource.reset();
}

function buildAuthSignature(): string | null {
  if (!get(isInitialized) || !get(isAuthenticated)) {
    return null;
  }

  const user = get(currentUser);
  if (!user) {
    return null;
  }

  return [
    user.id ?? '',
    user.companyId ?? '',
    user.role ?? '',
    user.isImpersonating ? '1' : '0'
  ].join(':');
}

function syncModulesAccessWithAuth(): void {
  const nextSignature = buildAuthSignature();
  if (nextSignature === authSignature) {
    return;
  }

  authSignature = nextSignature;

  if (!nextSignature) {
    resetModulesAccess();
    return;
  }

  void loadModulesAccess();
}

if (browser) {
  currentUser.subscribe(() => {
    syncModulesAccessWithAuth();
  });

  isAuthenticated.subscribe(() => {
    syncModulesAccessWithAuth();
  });

  isInitialized.subscribe(() => {
    syncModulesAccessWithAuth();
  });
}